import { recordCacheHit, recordCacheMiss, updateCacheFootprint } from '@/lib/recommendations/runtimeMetrics';

export type RecCapsConfig = {
  maxFavorites: number;
  maxRecent: number;
};

export type RecFeatureToggleConfig = {
  exploration: boolean;
  diversity: boolean;
  margin: boolean;
  station: boolean;
};

export type RecCategoryDiversityConfig = {
  enabled: boolean;
  minUniqueCategories: number;
  maxPerCategory: number;
  prioritizedCategories: string[];
};

export type RecConfig = {
  lambdaDiversity: number;
  alphaExplore: number;
  betaMargin: number;
  gammaStation: number;
  newItemMaxBoost: number;
  newItemHorizonImpressions: number;
  recencyTauHours: number;
  notInterestedTtlDays: number;
  caps: RecCapsConfig;
  enabled: RecFeatureToggleConfig;
  categoryDiversity: RecCategoryDiversityConfig;
};

export const defaultRecConfig: RecConfig = {
  lambdaDiversity: 0.25,
  alphaExplore: 14,
  betaMargin: 0.2,
  gammaStation: 0.15,
  newItemMaxBoost: 12,
  newItemHorizonImpressions: 60,
  recencyTauHours: 8,
  notInterestedTtlDays: 45,
  caps: { maxFavorites: 2, maxRecent: 1 },
  enabled: {
    exploration: true,
    diversity: true,
    margin: true,
    station: true,
  },
  categoryDiversity: {
    enabled: false,
    minUniqueCategories: 0,
    maxPerCategory: 3,
    prioritizedCategories: [],
  },
};

const getEnvValue = (key: string): string | undefined => {
  if (typeof process !== 'undefined' && process?.env?.[key] !== undefined) {
    return process.env[key];
  }
  if (typeof import.meta !== 'undefined') {
    const metaEnv = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
    if (metaEnv) {
      if (metaEnv[key] !== undefined) return metaEnv[key];
      const prefixedKey = `VITE_${key}`;
      if (metaEnv[prefixedKey] !== undefined) return metaEnv[prefixedKey];
    }
  }
  return undefined;
};

const parseNumberEnv = (key: string, fallback: number): number => {
  const raw = getEnvValue(key);
  if (!raw) return fallback;
  const parsed = Number(raw.trim());
  return Number.isFinite(parsed) ? parsed : fallback;
};

const parseBooleanEnv = (key: string, fallback: boolean): boolean => {
  const raw = getEnvValue(key);
  if (!raw) return fallback;
  const normalized = raw.trim().toLowerCase();
  if (['1', 'true', 'yes', 'on', 'enabled'].includes(normalized)) {
    return true;
  }
  if (['0', 'false', 'no', 'off', 'disabled'].includes(normalized)) {
    return false;
  }
  return fallback;
};

const parseStringListEnv = (key: string): string[] | undefined => {
  const raw = getEnvValue(key);
  if (!raw) return undefined;
  const entries = raw
    .split(',')
    .map((value) => value.trim().toLowerCase())
    .filter((value) => value.length > 0);
  return entries.length > 0 ? entries : undefined;
};

const buildEnvOverride = (): Partial<RecConfig> => {
  const lambda = parseNumberEnv('REC_LAMBDA_DIVERSITY', defaultRecConfig.lambdaDiversity);
  const alpha = parseNumberEnv('REC_ALPHA_EXPLORE', defaultRecConfig.alphaExplore);
  const beta = parseNumberEnv('REC_BETA_MARGIN', defaultRecConfig.betaMargin);
  const gamma = parseNumberEnv('REC_GAMMA_STATION', defaultRecConfig.gammaStation);
  const newItemMaxBoost = parseNumberEnv(
    'REC_NEW_ITEM_MAX_BOOST',
    defaultRecConfig.newItemMaxBoost,
  );
  const newItemHorizonImpressions = parseNumberEnv(
    'REC_NEW_ITEM_HORIZON_IMPRESSIONS',
    defaultRecConfig.newItemHorizonImpressions,
  );
  const maxFavorites = parseNumberEnv('REC_CAP_MAX_FAVORITES', defaultRecConfig.caps.maxFavorites);
  const maxRecent = parseNumberEnv('REC_CAP_MAX_RECENT', defaultRecConfig.caps.maxRecent);
  const tau = parseNumberEnv('REC_RECENCY_TAU_HOURS', defaultRecConfig.recencyTauHours);
  const suppressDays = parseNumberEnv('REC_NOT_INTERESTED_TTL_DAYS', defaultRecConfig.notInterestedTtlDays);
  const catMinUnique = parseNumberEnv(
    'REC_CATEGORY_MIN_UNIQUE',
    defaultRecConfig.categoryDiversity.minUniqueCategories,
  );
  const catMaxPer = parseNumberEnv(
    'REC_CATEGORY_MAX_PER',
    defaultRecConfig.categoryDiversity.maxPerCategory,
  );

  const toggles: Partial<RecFeatureToggleConfig> = {};
  (['exploration', 'diversity', 'margin', 'station'] as const).forEach((key) => {
    const envKey = `REC_ENABLE_${key.toUpperCase()}`;
    toggles[key] = parseBooleanEnv(envKey, defaultRecConfig.enabled[key]);
  });

  const categoryDiversity: Partial<RecCategoryDiversityConfig> = {
    enabled: parseBooleanEnv(
      'REC_CATEGORY_DIVERSITY_ENABLED',
      defaultRecConfig.categoryDiversity.enabled,
    ),
    minUniqueCategories: catMinUnique,
    maxPerCategory: catMaxPer,
    prioritizedCategories:
      parseStringListEnv('REC_CATEGORY_PRIORITY') ?? defaultRecConfig.categoryDiversity.prioritizedCategories,
  };

  return {
    lambdaDiversity: lambda,
    alphaExplore: alpha,
    betaMargin: beta,
    gammaStation: gamma,
    newItemMaxBoost,
    newItemHorizonImpressions,
    recencyTauHours: tau,
    notInterestedTtlDays: suppressDays,
    caps: {
      maxFavorites,
      maxRecent,
    },
    enabled: {
      exploration: toggles.exploration ?? defaultRecConfig.enabled.exploration,
      diversity: toggles.diversity ?? defaultRecConfig.enabled.diversity,
      margin: toggles.margin ?? defaultRecConfig.enabled.margin,
      station: toggles.station ?? defaultRecConfig.enabled.station,
    },
    categoryDiversity: {
      enabled: categoryDiversity.enabled ?? defaultRecConfig.categoryDiversity.enabled,
      minUniqueCategories:
        categoryDiversity.minUniqueCategories ?? defaultRecConfig.categoryDiversity.minUniqueCategories,
      maxPerCategory: categoryDiversity.maxPerCategory ?? defaultRecConfig.categoryDiversity.maxPerCategory,
      prioritizedCategories: categoryDiversity.prioritizedCategories ?? defaultRecConfig.categoryDiversity.prioritizedCategories,
    },
  };
};

const envOverride = buildEnvOverride();

type ConfigCacheEntry = {
  config: RecConfig;
  expiresAt: number;
};

const REC_CONFIG_CACHE_TTL_MS = parseNumberEnv('REC_CONFIG_CACHE_TTL_MS', 7 * 60 * 1000);
const configCache = new Map<string, ConfigCacheEntry>();

export const invalidateRecConfigCache = (tenantId?: string): void => {
  if (tenantId) {
    configCache.delete(tenantId);
  } else {
    configCache.clear();
  }
  updateCacheFootprint('config', configCache.size);
};

export const mergeRecConfig = (base: RecConfig, overrides?: Partial<RecConfig>): RecConfig => {
  if (!overrides) {
    return {
      ...base,
      caps: { ...base.caps },
      enabled: { ...base.enabled },
      categoryDiversity: { ...base.categoryDiversity },
    };
  }
  return {
    lambdaDiversity: overrides.lambdaDiversity ?? base.lambdaDiversity,
    alphaExplore: overrides.alphaExplore ?? base.alphaExplore,
    betaMargin: overrides.betaMargin ?? base.betaMargin,
    gammaStation: overrides.gammaStation ?? base.gammaStation,
    newItemMaxBoost: overrides.newItemMaxBoost ?? base.newItemMaxBoost,
    newItemHorizonImpressions:
      overrides.newItemHorizonImpressions ?? base.newItemHorizonImpressions,
    recencyTauHours: overrides.recencyTauHours ?? base.recencyTauHours,
    notInterestedTtlDays: overrides.notInterestedTtlDays ?? base.notInterestedTtlDays,
    caps: {
      maxFavorites: overrides.caps?.maxFavorites ?? base.caps.maxFavorites,
      maxRecent: overrides.caps?.maxRecent ?? base.caps.maxRecent,
    },
    categoryDiversity: {
      enabled: overrides.categoryDiversity?.enabled ?? base.categoryDiversity.enabled,
      minUniqueCategories:
        overrides.categoryDiversity?.minUniqueCategories ?? base.categoryDiversity.minUniqueCategories,
      maxPerCategory: overrides.categoryDiversity?.maxPerCategory ?? base.categoryDiversity.maxPerCategory,
      prioritizedCategories:
        overrides.categoryDiversity?.prioritizedCategories ?? base.categoryDiversity.prioritizedCategories,
    },
    enabled: {
      exploration: overrides.enabled?.exploration ?? base.enabled.exploration,
      diversity: overrides.enabled?.diversity ?? base.enabled.diversity,
      margin: overrides.enabled?.margin ?? base.enabled.margin,
      station: overrides.enabled?.station ?? base.enabled.station,
    },
  };
};

/**
 * Loads the recommendation configuration for a tenant. Today we fall back to globals/env overrides,
 * but the merge helpers keep this future-proof for per-tenant overrides sourced from the DB.
 */
export async function loadRecConfig(
  tenantId: string,
  tenantOverrides?: Partial<RecConfig>,
): Promise<RecConfig> {
  const now = Date.now();
  const cachedEntry = !tenantOverrides ? configCache.get(tenantId) : null;
  if (cachedEntry && cachedEntry.expiresAt > now) {
    recordCacheHit('config');
    return cachedEntry.config;
  }

  if (!tenantOverrides) {
    recordCacheMiss('config');
  }

  const merged = mergeRecConfig(defaultRecConfig, {
    ...envOverride,
    ...tenantOverrides,
  });

  if (!tenantOverrides) {
    configCache.set(tenantId, { config: merged, expiresAt: now + REC_CONFIG_CACHE_TTL_MS });
    updateCacheFootprint('config', configCache.size);
  }

  return merged;
}
